
import { Component, Vue, Prop, Watch, Inject } from 'vue-property-decorator';
import { CreateElement, VNode } from 'vue';
import { observeDom } from './../utils/dom-observer';
import { Validator } from 'vee-validate';
import Step from './step.vue';

const stepButtonHelper = {
    name: 'stepButtonHelper',
    props: {
        content: { type: [String, Array], default: '' },
        active: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        completed: { type: Boolean, default: false },
        setSize: { type: Number, default: null },
        position: { type: Number, default: null },
        linkClass: { default: null },
        itemClass: { default: null },
    },
    methods: {
        handleClick(event: Event): void {
            function stop(): void {
                event.preventDefault();
                event.stopPropagation();
            }

            if (event.type !== 'click') {
                stop();
                return;
            }

            // @ts-ignore
            if (this.disabled) {
                stop();
                return;
            }

            stop();
            // @ts-ignore
            this.$emit('click', event);
        },
    },
    render(h: CreateElement): VNode {
        const circle = h('span', {
           class: ['stepper-circle'],
        },
        // @ts-ignore
        this.position);

        const label = h('span', {
            class: ['stepper-label'],
        },
        // @ts-ignore
        this.content);

        const link = h('a', {
            // on: {
            //     // @ts-ignore
            //     click: this.handleClick,
            // },
        }, [circle, label] );

        // @ts-ignore
        return h('div', { class: ['step' , { active: this.active, completed: this.completed, disabled: this.disabled}]}, [link]);
    },
};

@Component
export default class Stepper extends Vue {
    @Inject('$validator') public $validator!: Validator;
    @Prop({ type: Number, required: true}) public value!: number;

    private currentStep: number = this.value;
    private steps: Array<Step> = new Array<Step>();

    @Watch('value')
    private valueChanged(newValue: number, oldValue: number): void {
        if (newValue === oldValue) {
            return;
        }

        if (typeof oldValue !== 'number') {
            oldValue = 0;
        }

        const direction = newValue < oldValue ? -1 : 1;

        this.setStep(newValue, false, direction);
    }
    private nextStep(): void {
        this.setStep(this.currentStep + 1, false, 1);
    }
    private previousStep(): void {
        this.setStep(this.currentStep - 1, false, -1);
    }
    private updateSteps(): void {
        this.steps = this.$slots!.default!.filter((node: VNode) => node && node.componentInstance && (node.componentInstance as Step).isStep)
                                        .map((node: VNode) => node.componentInstance as Step);

        let stepIndex: number | null = null;

        this.steps.forEach((step, index) => {
            if (step.localActive && !step.disabled) {
                stepIndex = index;
            }
        });

        if (stepIndex === null) {
            if (this.currentStep >= this.steps.length) {
                this.setStep(this.steps.length - 1, true, -1);
                return;
            } else if (this.steps[this.currentStep] && !this.steps[this.currentStep].disabled) {
                stepIndex = this.currentStep;
            }
        }

        if (stepIndex === null) {
            this.steps.forEach((step, index) => {
                if (!step.disabled && stepIndex === null) {
                    stepIndex = index;
                }
            });
        }

        this.setStep(stepIndex || 0, true, 0);
    }
    private setStep(index: number, force: boolean, direction: number = 0): void {
        direction = direction === 0 ? 0 : direction > 0 ? 1 : -1;

        if (!force && index === this.currentStep) {
            return;
        }

        const step = this.steps[index];

        if (!step) {
            // NOTE: This resets the v-model current step.
            this.$emit('input', this.currentStep);
            return;
        }

        if (step.disabled) {
            if (direction) {
                this.setStep(index + direction, force, direction);
            }

            return;
        }

        // Activate requested current step, and deactivte any old steps
        this.steps.forEach((s) => {
            if (step === s) {
                this.$set(s, 'localActive', true);
            } else {
                this.$set(s, 'localActive', false);
            }
        });

        this.currentStep = index;
    }
    public render(h: CreateElement): VNode | null {
        const steps = this.steps;

        // Navigation "buttons"
        const buttons = steps.map((step, index) => {
            return h(stepButtonHelper, {
                key: index,
                props: {
                    content: step.$slots.title || step.title,
                    active: step.localActive,
                    disabled: step.disabled,
                    completed: step.completed,
                    setSize: steps.length,
                    position: index + 1,
                    linkClass: step.titleLinkClass,
                    itemClass: step.titleItemClass,
                },
                on: {
                    click: (event: Event) => {
                        this.setStep(index, false);
                    },
                },
            });
        });

        const buttonsWithLines = buttons.map((button, index) => {
            if (buttons.length - 1 === index) {
                return [button];
            }

            return [button, h('div', { class: ['line']})];
        });

        // Navigation "button" wrapper
        const navs = h('div', {
            class: ['stepper-header'],
        }, [...buttonsWithLines, this.$slots.steps]);

        // "Empty" slot
        let empty: VNode;
        if (steps && steps.length) {
            // @ts-ignore
            empty = h(false);
        } else {
            empty = h('div', {}, this.$slots.empty);
        }

        // "Content"
        const content = h('div', {
            ref: 'stepsContainer',
            class: ['stepper-content'],
        }, [this.$slots.default, empty]);


        return h('div', {
            class: ['stepper'],
        }, [[navs], content]);
    }
    public mounted(): void {
        this.updateSteps();

        // @ts-ignore
        observeDom(this.$refs.stepsContainer, this.updateSteps.bind(this), { subtree: false });
    }
}
